import {IPersistUserDatas, IReg, REG} from "lib/commons/registry";
import {JSX} from "lib/commons/xml/dom";
import {Area, AREAS, CustomTagArea, IArea, IAreaPointer} from "lib/commons/areas";
import {IBasicUniversePointer} from "lib/core/universe";
import {JLastDatas} from "lib/commons/lastDatas";
import {OUserPrefCheckInit, OUserPrefSelectInit, UserPrefCheck, UserPrefEditor, UserPrefSelect} from "back/core/dialogs/userPrefEditor";
import {IO} from "lib/commons/io/io";
import {BaseElement} from "back/commons/basis";
import {IView} from "lib/commons/views";
import {DOMSH} from "lib/commons/xml/domsh";


/**
 * Configuration d'une page d'édition de prefs utilisateurs.
 * A déclarer dans le registre du contexte gérant l'affichage du dialogue d'édition des prefs.
 *
 * REG.reg.addToList(UserPrefEditorConfig.LIST_userPrefPages, "portletXyz", 1, new UserPrefPageConfig(portletReg).setAreasList("user:prefs:portletXyz").setLabel("..."))
 */
export class UserPrefPageConfig extends Area<UserPrefEditor, HTMLFormElement> {

	/** Code liste par défaut des pages d'édition des prefs */
	static LIST_userPrefPages = "user:prefs";
	/** Code de la page d'édition des prefs communes au front */
	static ENTRY_PAGE_front = "front";
	/** Code liste des preférences de la page générale "front" */
	static LIST_userPrefsFrontPage = "user:prefs:front";

	reg: IReg<IBasicUniversePointer>;

	get persistUserDatas(): IPersistUserDatas {return this.reg.getPersistUserStates()}

	/** Areas constituant les pref éditables. */
	areas?: Array<IArea<IUserPrefEditorCtx, BaseElement> | (() => Promise<IArea<IUserPrefEditorCtx, BaseElement>>)>

	/**
	 * Liste des areas constituant les pref éditables (si areas null).
	 * Type des entrées de la liste : IArea<IUserPrefEditorCtx, BaseElement> | (()=>Promise<IArea<IUserPrefEditorCtx, BaseElement>>)
	 */
	areasList?: string;

	constructor(reg: IReg<IBasicUniversePointer>) {
		super();
		this.reg = reg;
	}

	setAreasList(areasList: string): this {
		this.areasList = areasList;
		return this;
	}

	needAsync(ctx: UserPrefEditor): boolean {return true}

	buildBody(ctx: UserPrefEditor): HTMLFormElement {throw Error("not used")}

	async loadBody(ctx: UserPrefEditor, lastDatas?: JLastDatas): Promise<HTMLFormElement> {
		const form = <form/> as HTMLFormElement & IView & IAreaPointer<UserPrefEditor, HTMLFormElement>;
		const areas: (IArea<IUserPrefEditorCtx> | Promise<IArea<IUserPrefEditorCtx>>)[] = (this.areas || this.reg.getList(this.areasList)).map((a) => typeof a === "function" ? a() : a);
		await AREAS.applyLayout(form, await Promise.all(areas), {userPrefCtn: ctx, editorConfig: this, reg: this.reg});
		form.area = this;
		form.areaContext = ctx;
		form.onViewBeforeHide = function(this:HTMLFormElement) {return DOMSH.findHost<UserPrefEditor>(this)?.onViewBeforeHide() ?? true}
		form.onViewWaitForHide = function(this:HTMLFormElement) {return DOMSH.findHost<UserPrefEditor>(this)?.onViewWaitForHide() || Promise.resolve(true)}
		return form;
	}
}

/** Page par défaut*/
REG.reg.addToList(UserPrefPageConfig.LIST_userPrefPages, UserPrefPageConfig.ENTRY_PAGE_front, 1, new UserPrefPageConfig(REG.reg).setAreasList(UserPrefPageConfig.LIST_userPrefsFrontPage).setLabel("Général"), 1);


/**
 * Area pour une pref de type checkBox.
 * A déclarer de préférence dans le registre du portlet stockant ces prefs.
 *
 * portletReg.addToList("user:prefs:portletXyz", "pref1", 1, new CheckPrefArea("keyPref1").setLabel("..."))
 */
export class CheckPrefArea extends CustomTagArea<IUserPrefEditorCtx, UserPrefCheck, OUserPrefCheckInit> {

	isReloadAppNeeded = false;

	invertCheck = false;

	checkStateIfNone = false;

	constructor(prefKey: string) {
		super(prefKey);
		this.requireLib(lib);
		this.tagName = "c-userpref-check";
	}

	setReloadAppNeeded(): this {
		this.isReloadAppNeeded = true;
		return this;
	}

	setInvertCheck(): this {
		this.invertCheck = true;
		return this;
	}

	setCheckStateIfNone(st:boolean): this {
		this.checkStateIfNone = st;
		return this;
	}

	getInit(ctx: IUserPrefEditorCtx): OUserPrefCheckInit {
		return {reg: ctx.reg, area: this, areaContext: ctx};
	}
}

/**
 * Area pour une pref de type sélection dans une liste.
 * A déclarer de préférence dans le registre du portlet stockant ces prefs.
 *
 * portletReg.addToList("user:prefs:portletXyz", "pref1", 1, new SelectPrefArea("keyPref1").setLabel("..."))
 */
export class SelectPrefArea extends CustomTagArea<IUserPrefEditorCtx, UserPrefSelect, OUserPrefSelectInit> {

	isReloadAppNeeded = false;

	_buildOptions: (selectElt: HTMLSelectElement, currentVal: string | null) => void;

	constructor(prefKey: string) {
		super(prefKey);
		this.requireLib(lib);
		this.tagName = "c-userpref-select";
	}

	setBuildOptions(buildOptions: (selectElt: HTMLSelectElement, currentVal: string | null) => void): this {
		this._buildOptions = buildOptions;
		return this;
	}

	buildOptions(selectElt: HTMLSelectElement, currentVal: string | null): void {
		this._buildOptions(selectElt, currentVal);
	}

	setReloadAppNeeded(): this {
		this.isReloadAppNeeded = true;
		return this;
	}

	getInit(ctx: IUserPrefEditorCtx): OUserPrefSelectInit {
		return {reg: ctx.reg, area: this, areaContext: ctx};
	}
}


const lib = IO.asEndPoint("/@back@/core/dialogs/userPrefEditor.js");

/** Contexte d'exécution d'une page d'édition des userPref. */
export interface IUserPrefEditorCtx {
	/** */
	editorConfig: UserPrefPageConfig

	/** Correspond à editorConfig.reg (recopié ici pour eval perms) */
	reg: IReg<IBasicUniversePointer>

	/** Contexte graphique de l'éditeur. */
	userPrefCtn: UserPrefEditor
}
